/**
 * Market selector — chooses which Stake ACP backend receives the query.
 * The selected market is sent in the API request body so the backend can
 * route to the correct relay worker (one per market).
 *
 * Primary market persisted in localStorage. Mirrors the macroTranslations
 * primary-language pattern so the UI feels consistent.
 */
import { useState, useCallback } from 'react';
import TOOLS from '../pages/tools/toolsList';

export const MARKETS = [
  { code: 'com', label: 'Stake.com', flag: '/flags/world.svg' },
  { code: 'us', label: 'Stake.us', flag: '/flags/us.svg' },
  { code: 'dk', label: 'Stake.dk', flag: '/flags/dk.svg', comingSoon: true },
  { code: 'mx', label: 'Stake.mx', flag: '/flags/mx.svg' },
  { code: 'ar', label: 'Stake.ar', flag: '/flags/ar.svg' },
  // Moon (moon.com) has no ACP in Clara; only tools that list it in
  // supportedMarkets (Optimove/Insider) offer it. Its CRM is Insider only.
  { code: 'moon', label: 'Moon', flag: '/flags/moon.svg' },
];

/** Markets whose CRM is Insider only — no Optimove instance, no ACP lookups. */
export const INSIDER_ONLY_MARKETS = ['moon'];

export const ACTIVE_MARKETS = MARKETS.filter(m => !m.comingSoon);

/**
 * Languages available per market. Stake.com supports the full set;
 * stake.us is English only (US-facing platform).
 * Tools that show a language dropdown should filter MACRO_LANGUAGES
 * against this list when market !== 'com'.
 */
export const MARKET_LANGUAGES = {
  com: ['en', 'ja', 'zh', 'de', 'es', 'tr', 'ar', 'fr', 'ru'],
  us: ['en'],
  mx: ['es'],
  ar: ['es'],
  moon: ['en'],
};

/**
 * Per-market settlement currency. Drives amount formatting in the tools.
 * com/us are USD-denominated (us additionally surfaces SC/GC); the regulated
 * LATAM markets settle in local fiat — mx in MXN, ar in ARS.
 */
export const MARKET_CURRENCY = { com: 'USD', us: 'USD', mx: 'MXN', ar: 'ARS', moon: 'USD' };

/**
 * Per-market public site domain — used to localize stake.com links inside
 * support macros (mirrors the existing .us → stake.us domain swap).
 * Argentina's apex is stake.bet.ar (canonical host pba.stake.bet.ar).
 */
export const MARKET_DOMAIN = { com: 'stake.com', us: 'stake.us', mx: 'stake.mx', ar: 'stake.bet.ar', moon: 'moon.com' };

/**
 * Per-market ACP (admin control panel) host — used to build deep links into a
 * user's admin pages (e.g. the bonuses page for a manual level-up payout check).
 */
export const MARKET_ACP_DOMAIN = {
  com: 'buyconcorde.club',
  us: 'redbagsalad.com',
  mx: 'acp.stake.mx',
  ar: 'acp.pba.stake.bet.ar',
};

const LS_KEY = 'relay-primary-market';

export function getPrimaryMarket() {
  try { return localStorage.getItem(LS_KEY) || 'com'; }
  catch { return 'com'; }
}

export function setPrimaryMarket(code) {
  try { localStorage.setItem(LS_KEY, code); } catch {}
}

/**
 * Hook — mirrors useMacroLanguage. Tracks the currently-selected market
 * (transient, per-tool) and the persisted "primary" market. makePrimary()
 * pins the current selection so it becomes the default across sessions.
 */
export function useMarket() {
  const [market, setMarket] = useState(getPrimaryMarket);
  const [primaryMarket, setPrimaryState] = useState(getPrimaryMarket);

  const makePrimary = useCallback(() => {
    setPrimaryMarket(market);
    setPrimaryState(market);
  }, [market]);

  return { market, setMarket, primaryMarket, makePrimary };
}

/**
 * Markets a given tool supports, read from the Tools registry
 * (pages/tools/toolsList.js). Falls back to com-only for an unknown tool.
 */
export function getToolMarkets(toolId) {
  const t = TOOLS.find((x) => x.id === toolId);
  return (t && t.supportedMarkets && t.supportedMarkets.length) ? t.supportedMarkets : ['com'];
}

/**
 * Market hook scoped to a single tool. Same shape as useMarket() but the
 * selectable set is limited to the tool's supportedMarkets, and the initial
 * selection is clamped into that set — so a user whose primary market is one
 * this tool doesn't serve lands on a valid market instead of a broken query.
 * Returns `allowedMarkets` for the MarketSelector dropdown to render.
 */
export function useToolMarket(toolId) {
  const allowedMarkets = getToolMarkets(toolId);
  const [market, setMarket] = useState(() => {
    const p = getPrimaryMarket();
    return allowedMarkets.includes(p) ? p : allowedMarkets[0];
  });
  const [primaryMarket, setPrimaryState] = useState(getPrimaryMarket);

  const makePrimary = useCallback(() => {
    setPrimaryMarket(market);
    setPrimaryState(market);
  }, [market]);

  return { market, setMarket, primaryMarket, makePrimary, allowedMarkets };
}
